import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../../../config';
import { useOrganization } from '../../../context/OrganizationContext';
import { usePlan } from '../../../context/PlanContext';
import { openCommandGlobal } from '../../../context/CommandContext';
import OpportunityCard from './OpportunityCard';
import TemplateCard from './TemplateCard';

import {
  ArrowRight,
  ArrowSquareOut,
  CaretRight,
  CircleNotch,
  Copy,
  Cpu,
  FileText,
  Lightbulb,
  Lock,
  MagnifyingGlass,
  Phone,
  Robot,
  Sparkle,
  Target
} from '@phosphor-icons/react';
/**
 * EntryPointSelector - Choose how to start creating an agent
 * 
 * Entry Points (Unified Agent Builder Consolidation):
 * - AI Builder → AI Chat tab (conversational start)
 * - Voice Agent → Specialized S2S wizard (external)
 * - From Opportunity → Visual Flow tab (capabilities injected)
 * - From Template → Visual Flow tab (template injected)
 * - Start from Scratch → Config tab (no prefill)
 * 
 * Architecture: Routes to specialized wizards for complex agent types (voice S2S)
 * while keeping simpler agents in the unified builder.
 */
const EntryPointSelector = ({ onSelect }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const { isFreePlan, isProOrHigher } = usePlan();
  const [opportunities, setOpportunities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('opportunities'); // 'opportunities' | 'templates'

  // Auto-route to AI Builder if prompt is provided from dashboard
  useEffect(() => {
    const prompt = searchParams.get('prompt');
    if (prompt && onSelect) {
      onSelect('ai_builder', { initialPrompt: prompt });
    }
  }, [searchParams, onSelect]);

  useEffect(() => {
    const loadData = async () => {
      if (!activeOrganization?.organization_id) return;
      
      try {
        // Load opportunities and templates in parallel
        const [oppsRes, templatesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/process/opportunities?limit=6`, {
            credentials: 'include',
            headers: { 'X-Organization-Id': activeOrganization.organization_id }
          }),
          fetch(`${BACKEND_URL}/api/templates?limit=6`, {
            credentials: 'include'
          })
        ]);
        
        if (oppsRes.ok) {
          const oppsData = await oppsRes.json();
          setOpportunities(oppsData.opportunities || []);
        }
        
        if (templatesRes.ok) {
          const templatesData = await templatesRes.json();
          setTemplates(templatesData.templates || templatesData || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [activeOrganization]);

  // Entry points with their target tabs (per consolidation spec)
  const entryPoints = [
    {
      id: 'ai',
      title: 'AI Agent Builder',
      description: 'Use Command to create agents with natural language',
      icon: Sparkle,
      color: 'from-indigo-500 to-purple-500',
      iconBg: 'bg-indigo-500/20',
      iconColor: 'text-indigo-400',
      recommended: true,
      recommendedLabel: 'Process Agents',
      targetTab: 'chat' // Opens in AI Chat tab
    },
    {
      id: 'voice',
      title: 'Voice Agent',
      description: 'Build a phone-based S2S agent for calls & bookings',
      icon: Phone,
      color: 'from-purple-500 to-pink-500',
      iconBg: 'bg-purple-500/20',
      iconColor: 'text-purple-400',
      specialized: true,
      specializedLabel: 'Voice Calls',
      agentType: 'voice_s2s'
    },
    {
      id: 'operator',
      title: 'Orion - Operator Agent',
      description: 'Build an agent that operates your local machine',
      icon: null,
      customIcon: '/orion-icon-192.png',
      color: 'from-pink-500 to-red-500',
      iconBg: 'bg-transparent',
      iconColor: 'text-pink-400',
      specialized: true,
      agentType: 'operator',
      badge: 'Personal Assistant'
    },
    {
      id: 'opportunity',
      title: 'From Opportunity',
      description: 'Build from an identified automation opportunity',
      icon: Target,
      color: 'from-emerald-500 to-teal-500',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-400',
      hasItems: opportunities.length > 0,
      targetTab: 'visual', // Opens in Visual Flow tab
      secondary: true
    },
    {
      id: 'template',
      title: 'From Automation',
      description: 'Browse pre-built automations to get started quickly',
      icon: Copy,
      color: 'from-amber-500 to-orange-500',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-400',
      hasItems: templates.length > 0,
      targetTab: 'visual', // Opens in Visual Flow tab
      secondary: true
    }
    // Note: "Start from Scratch" removed - Config tab is always accessible in builder
  ];

  // Separate primary and secondary entry points
  const primaryEntryPoints = entryPoints.filter(p => !p.secondary);
  const secondaryEntryPoints = entryPoints.filter(p => p.secondary);

  const filteredOpportunities = opportunities.filter(opp => 
    opp.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opp.one_liner?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTemplates = templates.filter(tmpl =>
    tmpl.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tmpl.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle voice agent creation - creates draft and redirects to specialized wizard
  const handleVoiceAgentCreate = async () => {
    if (!activeOrganization?.organization_id) {
      console.error('No active organization');
      return;
    }

    try {
      // Create a draft voice agent
      const response = await fetch(`${BACKEND_URL}/api/agents`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization.organization_id
        },
        body: JSON.stringify({
          name: 'New Voice Agent',
          short_description: 'Voice agent for phone-based interactions',
          capability_profile: 'live_voice_s2s_agent',
          accepted_inputs: ['audio', 'text'],
          status: 'draft',
          is_active: false,
          org_id: activeOrganization.organization_id
        })
      });

      if (response.ok) {
        const agent = await response.json();
        const agentId = agent.id || agent.agent_id;
        // Navigate to the specialized voice wizard
        navigate(`/partner-pro/ai-workforce/agents/${agentId}/wizard`);
      } else {
        console.error('Failed to create voice agent');
      }
    } catch (error) {
      console.error('Error creating voice agent:', error);
    }
  };

  return (
    <div className="flex-1 overflow-auto p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-4"
          >
            <Sparkle className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-indigo-400 font-medium">Unified Agent Builder</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl font-bold text-white mb-3"
          >
            Create an AI Agent
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400"
          >
            Choose how you'd like to start
          </motion.p>
        </div>

        {/* Primary Entry Point Cards - Main Agent Types */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {primaryEntryPoints.map((point, index) => (
            <motion.button
              key={point.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              onClick={() => {
                // Check if Voice Agent is locked for Basic tier
                if (point.id === 'voice' && isFreePlan()) {
                  // Don't navigate - show upgrade message handled by UI
                  return;
                }
                
                if (point.id === 'ai') {
                  // AI Agent Builder → Open Command as the primary creation interface
                  navigate('/dashboard', { replace: true });
                  setTimeout(() => openCommandGlobal(), 300);
                } else if (point.specialized && point.id === 'voice') {
                  // Voice agents use specialized wizard - create draft agent first
                  handleVoiceAgentCreate();
                } else if (point.specialized && point.id === 'operator') {
                  // Operator agents - trigger the onSelect callback with special 'operator' type
                  // The parent component (AgentBuilder) will handle opening the CreateOperatorModal
                  onSelect('operator');
                } else {
                  onSelect(point.id);
                }
              }}
              className={`
                relative p-6 rounded-xl border text-left transition-all group
                ${point.id === 'voice' && isFreePlan()
                  ? 'border-slate-700/50 bg-slate-800/30 cursor-not-allowed opacity-60'
                  : point.recommended 
                    ? 'border-indigo-500/50 bg-indigo-500/5 hover:bg-indigo-500/10' 
                    : point.specialized
                      ? 'border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10 hover:border-purple-500/50'
                      : 'border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-white/20'
                }
              `}
              data-testid={`entry-point-${point.id}`}
            >
              {/* Lock badge for Basic tier on Voice Agent */}
              {point.id === 'voice' && isFreePlan() && (
                <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-slate-600 text-[10px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Builder & Above
                </div>
              )}
              {point.recommended && (
                <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-indigo-500 text-[10px] font-bold text-white uppercase tracking-wider">
                  {point.recommendedLabel || 'Recommended'}
                </div>
              )}
              {point.specialized && !point.badge && !(point.id === 'voice' && isFreePlan()) && (
                <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-purple-500 text-[10px] font-bold text-white uppercase tracking-wider flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {point.specializedLabel || 'Specialized'}
                </div>
              )}
              {point.badge && (
                <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-gradient-to-r from-pink-500 to-red-500 text-[10px] font-bold text-white uppercase tracking-wider flex items-center gap-1">
                  {point.customIcon ? (
                    <img src={point.customIcon} alt="" className="w-3 h-3 rounded-sm" />
                  ) : (
                    <Cpu className="w-3 h-3" />
                  )}
                  {point.badge}
                </div>
              )}
              
              <div className="flex items-start gap-4">
                <div className={`${point.customIcon ? 'p-0' : 'p-2'} rounded-xl ${point.id === 'voice' && isFreePlan() ? 'bg-slate-700/30' : point.iconBg} ${point.customIcon ? 'overflow-hidden' : ''}`}>
                  {point.id === 'voice' && isFreePlan() ? (
                    <Lock className="w-8 h-8 text-slate-500" />
                  ) : point.customIcon ? (
                    <img src={point.customIcon} alt={point.title} className="w-11 h-11 rounded-xl object-cover" />
                  ) : (
                    <point.icon className={`w-8 h-8 ${point.iconColor}`} />
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className={`font-semibold mb-1 flex items-center gap-2 ${point.id === 'voice' && isFreePlan() ? 'text-slate-400' : 'text-white'}`}>
                    {point.title}
                    {point.id === 'voice' && isFreePlan() ? (
                      <Lock className="w-4 h-4 text-slate-500" />
                    ) : point.specialized ? (
                      <ArrowSquareOut 
                        className="w-4 h-4 text-purple-400 group-hover:text-purple-300 transition-all" 
                      />
                    ) : (
                      <CaretRight 
                        className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all" 
                      />
                    )}
                  </h3>
                  <p className={`text-sm ${point.id === 'voice' && isFreePlan() ? 'text-slate-500' : 'text-slate-400'}`}>
                    {point.id === 'voice' && isFreePlan() 
                      ? 'Voice Agents are available on Builder and higher plans' 
                      : point.description
                    }
                  </p>
                  {point.specialized && !(point.id === 'voice' && isFreePlan()) && (
                    <p className="text-xs text-purple-400 mt-2">
                      {point.id === 'operator' ? 'Opens dedicated setup wizard' : 'Opens dedicated voice wizard'}
                    </p>
                  )}
                  {point.id === 'voice' && isFreePlan() && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/pricing');
                      }}
                      className="text-xs text-indigo-400 hover:text-indigo-300 mt-2 underline"
                    >
                      Upgrade to create AI voice assistants →
                    </button>
                  )}
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Divider with label */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10"></div>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Or start from</span>
          <div className="flex-1 h-px bg-white/10"></div>
        </div>

        {/* Secondary Entry Points - Start From Options */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {secondaryEntryPoints.map((point, index) => (
            <motion.button
              key={point.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              onClick={() => {
                if (point.id === 'opportunity') {
                  // Switch to opportunities tab and scroll down
                  setActiveTab('opportunities');
                  // Scroll to the quick access section
                  setTimeout(() => {
                    document.getElementById('quick-access-section')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                } else if (point.id === 'template') {
                  // Navigate to Automations page
                  navigate('/automations');
                } else {
                  onSelect(point.id);
                }
              }}
              className="relative p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-white/20 text-left transition-all group"
              data-testid={`entry-point-${point.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${point.iconBg}`}>
                  <point.icon className={`w-5 h-5 ${point.iconColor}`} />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-1 flex items-center gap-2 text-sm">
                    {point.title}
                    <CaretRight 
                      className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all" 
                    />
                  </h3>
                  <p className="text-xs text-slate-400">
                    {point.description}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Quick Access Sections - Always show for navigation */}
        <div className="space-y-4" id="quick-access-section">
          {/* Tabs */}
          <div className="flex items-center gap-4 border-b border-white/10 pb-2">
              <button
                onClick={() => setActiveTab('opportunities')}
                className={`flex items-center gap-2 pb-2 border-b-2 transition-colors ${
                  activeTab === 'opportunities' 
                    ? 'border-emerald-400 text-emerald-400' 
                    : 'border-transparent text-slate-500 hover:text-white'
                }`}
              >
                <Target className="w-4 h-4" />
                <span className="text-sm font-medium">Opportunities</span>
                {opportunities.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px]">
                    {opportunities.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`flex items-center gap-2 pb-2 border-b-2 transition-colors ${
                  activeTab === 'templates' 
                    ? 'border-amber-400 text-amber-400' 
                    : 'border-transparent text-slate-500 hover:text-white'
                }`}
              >
                <Copy className="w-4 h-4" />
                <span className="text-sm font-medium">Automations</span>
                {templates.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px]">
                    {templates.length}
                  </span>
                )}
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 
                  text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50
                  focus:ring-2 focus:ring-indigo-500/20"
                data-testid="entry-point-search"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12" data-testid="entry-point-loading">
                <CircleNotch className="w-6 h-6 text-indigo-400 animate-spin" />
              </div>
            ) : (
              <div className="space-y-3" data-testid="entry-point-list">
                {/* Opportunities Tab */}
                {activeTab === 'opportunities' && (
                  <>
                    {filteredOpportunities.length > 0 ? (
                      filteredOpportunities.slice(0, 6).map((opp, idx) => (
                        <OpportunityCard
                          key={opp._id || opp.id || idx}
                          opportunity={opp}
                          onClick={() => onSelect('opportunity', { id: opp._id || opp.id, data: opp })}
                          index={idx}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Target className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">No opportunities found</p>
                        <p className="text-slate-600 text-xs mt-1">
                          Run an assessment to discover automation opportunities
                        </p>
                      </div>
                    )}
                    
                    {opportunities.length > 6 && (
                      <button
                        onClick={() => navigate('/opportunities')}
                        className="w-full p-3 rounded-lg border border-white/10 hover:border-emerald-500/30 
                          hover:bg-emerald-500/5 text-slate-400 hover:text-emerald-400 
                          text-sm flex items-center justify-center gap-2 transition-all"
                      >
                        View all {opportunities.length} opportunities
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}

                {/* Templates Tab */}
                {activeTab === 'templates' && (
                  <>
                    {filteredTemplates.length > 0 ? (
                      filteredTemplates.slice(0, 6).map((tmpl, idx) => (
                        <TemplateCard
                          key={tmpl._id || tmpl.template_id || idx}
                          template={tmpl}
                          onClick={() => onSelect('template', { id: tmpl._id || tmpl.template_id, data: tmpl })}
                          index={idx}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Copy className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">Browse the Agent Marketplace</p>
                        <p className="text-slate-600 text-xs mt-1 mb-4">
                          Find pre-built automations to get started quickly
                        </p>
                        <button
                          onClick={() => navigate('/partner-pro/ai-workforce/marketplace-v2')}
                          className="px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/30 
                            text-amber-400 hover:bg-amber-500/30 text-sm flex items-center gap-2 mx-auto transition-all"
                        >
                          <Sparkle className="w-4 h-4" />
                          Open Agent Marketplace
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {templates.length > 6 && (
                      <button
                        onClick={() => navigate('/partner-pro/ai-workforce/marketplace-v2')}
                        className="w-full p-3 rounded-lg border border-white/10 hover:border-amber-500/30 
                          hover:bg-amber-500/5 text-slate-400 hover:text-amber-400 
                          text-sm flex items-center justify-center gap-2 transition-all"
                      >
                        View all templates in Marketplace
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default EntryPointSelector;
